import React, { memo, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { validateOtpDigit } from '@/utils/formValidation';
import { FieldErrors } from 'react-hook-form';
import Input from './Input';
import Map from '@/components/Map';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import classNames from 'classnames';

type InputProps = {
  className?: string;
  dataTestId?: string;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
  boxNumber?: number;
  label?: string;
  name: string;
  errors?: FieldErrors;
  value?: string | number;
  type?: 'text' | 'number';
  onSet: Function;
  emptyInputBox?: boolean;
  onSubmit?: Function;
  handleResendClick?: Function;
  data?: { success: boolean; message: string };
};

const OtpInput = ({
  name,
  onSet,
  className,
  label,
  errors,
  boxNumber = 4,
  size = 'base',
  type = 'text',
  emptyInputBox = false,
  data,
  ...restProps
}: InputProps) => {
  const { register, setFocus } = useForm();
  const [OTP, setOTP] = useState(new Array(boxNumber).fill(''));
  const { fireGTMEvent } = useGTM();

  useEffect(() => {
    setFocus(`otpDigit0`);
    if (emptyInputBox) {
      setOTP((prevOTP) => prevOTP.map(() => ''));
    }
  }, [emptyInputBox]);

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      const newOTP = [...OTP];
      newOTP[index] = '';
      setOTP(newOTP);
      onSet(newOTP.join(''));
      // setOTP((prevOTP) => {
      //   const newOTP = [...prevOTP];
      //   newOTP[index] = '';
      //   return newOTP;
      // });
      // onSet(OTP.join(''));
      setTimeout(() => {
        setFocus(`otpDigit${index - 1}`);
      }, 0);
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setTimeout(() => {
        setFocus(`otpDigit${index - 1}`);
      }, 0);
    } else if (e.key === 'ArrowRight' && index < boxNumber - 1) {
      setTimeout(() => {
        setFocus(`otpDigit${index + 1}`);
      }, 0);
    } else {
      if (OTP[index] !== '') {
        setFocus(`otpDigit${index + 1}`);
      }
    }
  };

  const handleInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const valueLength = value.length;
    if (valueLength === boxNumber) {
      setOTP(value.split(''));
      setFocus(`otpDigit${valueLength - 1}`);
      onSet(value);
      return;
    }

    if (OTP[index] !== '') {
      setFocus(`otpDigit${index + 1}`);
      return;
    }
    if (valueLength == 1) OTP[index] = value;
    if (value !== '' && index < boxNumber - 1) {
      setFocus(`otpDigit${index + 1}`);
    }
    setOTP(OTP);
    onSet(OTP.join(''));
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault(); // Prevent entering non-numeric characters
    }
  };

  // ON PASTE
  // const onPaste = (e: React.ClipboardEvent, index: number) => {
  //   e.preventDefault();
  //   const char = e.clipboardData.getData('text').split('');
  //   const [one = '', two = '', three = '', four = ''] = char;
  //   const otpLength = char.length;
  //   setOTP([one, two, three, four]);
  //   setFocus(`otpDigit${otpLength - 1}`);
  //   onSet([one,two, three, four].join(''));
  // };

  return (
    <Map
      className="flex gap-s4 w-auto "
      list={OTP}
      render={(digit, index) => (
        <Input
          key={index}
          id={`otp-input-${index}`}
          type="number"
          autoComplete="one-time-code"
          data-testid={`otp-input-${index}`}
          value={digit}
          {...register(`otpDigit${index}`, {
            pattern: /^[0-9]{1}$/,
            validate: {
              validateOtpDigit,
            },
          })}
          maxLength={1}
          onChange={(e) => handleInputChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onKeyPress={handleKeyPress}
          className={classNames('border rounded-md text-xs text-center w-full', {
            ['border-red-500']: !data?.success && data?.message,
          })}
          style={{ paddingLeft: 0, paddingRight: 0 }}
        />
      )}
    />
  );
};

export default memo(OtpInput);
